import { useCallback, useEffect, useRef, useState } from 'react'
import { isPrintingEnabled } from './utils/printing'
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom'
import { AuthProvider, useAuth } from './context/AuthContext'
import { UpgradeProvider } from './context/UpgradeContext'
import { CatalogProvider } from './context/CatalogContext'
import { OrdersProvider } from './context/OrdersContext'
import { CartProvider } from './context/CartContext'
import { OpenTicketsProvider } from './context/OpenTicketsContext'
import { CariProvider } from './context/CariContext'
import {
  ProtectedRoute,
  OwnerRoute,
  MenuRoute,
  SuperadminRoute,
  CariRoute,
  TrackRoute,
  KitchenRoute,
  KioskRoute,
} from './components/ProtectedRoute'
import { TrackPage } from './pages/TrackPage'
import { KitchenPage } from './pages/KitchenPage'
import { KioskPage } from './pages/KioskPage'
import { PairScreenPage } from './pages/PairScreenPage'
import { POSLayout } from './components/Layout/POSLayout'
import { Receipt, type ReceiptKind } from './components/receipt/Receipt'
import { useTicketPrinter } from './hooks/useTicketPrinter'
import { printReceipt } from './utils/printMode'
import { getNetworkPrint } from './services/printBridge'
import { printReceiptToNetwork } from './services/networkPrint'
import { PRINT_FAILED_EVENT, PrintErrorBanner } from './components/PrintErrorBanner'
import { getPrintedIds } from './utils/deviceId'
import { recordPrint } from './utils/printHistory'
import { RecentSlipsDialog, RECENT_SLIPS_EVENT } from './components/print/RecentSlipsDialog'
import { startDrainer } from './services/sync/drainer'
import { FALLBACK_TENANT, resolveTenantId } from './services/tenant'
import { LoginPage } from './pages/LoginPage'
import { ActivatePage } from './pages/ActivatePage'
import { CariPage } from './pages/CariPage'
import { MenuPage } from './pages/MenuPage'
import { LoyaltyCardPage } from './pages/LoyaltyCardPage'
import { KioskTrackPage } from './pages/KioskTrackPage'
import { KioskDoneDemo, KioskTillDemo, KioskTrackDemo } from './pages/KioskTrackDemo'
import { BalanceDemo, FinanceDemo, SubscriptionsDemo, TenantWindowDemo } from './pages/SuperadminDemo'
import { SalePage } from './pages/SalePage'
import { MenuAdminPage } from './pages/MenuAdminPage'
import { PrintersPage } from './pages/PrintersPage'
import { HomePage } from './pages/HomePage'
import { DayReportPage } from './pages/DayReportPage'
import { SettingsPage } from './pages/SettingsPage'
import { SuperadminPage } from './pages/SuperadminPage'
import { SetPasswordPage } from './pages/SetPasswordPage'
import { toDayKey } from './utils/dayKey'
import type { Order } from './types'
import { setViewMode } from './services/viewMode'

/**
 * Holds which day the report is showing.
 *
 * Above OrdersProvider rather than inside DayReportPage, because the provider
 * is what runs the query, the page only reads what it produced, so the choice
 * has to be made on this side of it.
 */
function ReportRoute({ onPrint }: { onPrint: (order: Order, kind?: ReceiptKind) => void }) {
  const [day, setDay] = useState(toDayKey())
  return (
    <OrdersProvider full dayKey={day}>
      <POSLayout title="Rapor">
        <DayReportPage onPrint={onPrint} dayKey={day} onPickDay={setDay} />
      </POSLayout>
    </OrdersProvider>
  )
}

function Shell() {
  const { tenantName, receiptSettings, cashReceiptSettings, printers, isSuperadmin } = useAuth()
  // Sistem yöneticisi bir kurumun ekranlarında: görüntüleme modu (viewMode.ts).
  // Kurumdan çıkınca (Sistem Yönetimi'ne dönünce) kapanıyor.
  useEffect(() => {
    setViewMode(isSuperadmin)
    return () => setViewMode(false)
  }, [isSuperadmin])
  // print sabit bir callback; yazıcı listesi canlı değişiyor.
  const printersRef = useRef(printers)
  printersRef.current = printers
  const [receipt, setReceipt] = useState<Order | null>(null)
  // Which of the two slips the hidden print area is currently holding. Only
  // one is ever mounted, so the print stylesheet has nothing to choose between.
  const [kind, setKind] = useState<ReceiptKind>('station')
  const cashReady = cashReceiptSettings.splitByStation !== undefined


  const print = useCallback(
    (order: Order, as: ReceiptKind = 'station') => {
      // Yazıcısız kurum (Ayarlar > Yazıcılar): hiçbir yoldan basılmıyor,
      // yazdırma penceresi de açılmıyor.
      if (!isPrintingEnabled()) return
      setKind(as)
      setReceipt(order)
      // "Son fişler" listesi; tekrar basımlar listeye ikinci kez girmiyor.
      if (!order.reprint) recordPrint(order, as)

      // window.print() is synchronous and blocking, so firing it in the same
      // tick as setState prints the *previous* receipt (or a blank one). The
      // first rAF queues after React commits, the second after paint.
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          // Measured after paint, from the slip that is about to be printed:
          // its height in millimetres is what the page rule has to state, and
          // that is only knowable once the lines have been laid out. A single
          // station's slip prints as one length of paper; several still break
          // between slips, which is what r-slip's page break is for.
          const root = document.getElementById('receipt-root')
          // Bütün tezgahları "fiş basılmasın" olan bir sipariş: basılacak fiş yok,
          // boş bir sayfa da çıkmasın.
          if (!root?.querySelector('.r-slip')) return
          const tallest = root
            ? Math.max(
                ...[...root.querySelectorAll('.r-slip')].map((el) => el.scrollHeight),
                0,
              )
            : 0
          // Ağ yazıcıları (20 Eki 2026): bu bilgisayar basıyorsa fiş tezgah
          // tezgah resme çevrilip Yazıcı Köprüsü'ne gidiyor; tarayıcının
          // yazdırması hiç açılmıyor.
          if (getNetworkPrint() && printersRef.current.length > 0) {
            void printReceiptToNetwork(as, printersRef.current).then(({ failed }) => {
              if (failed.length > 0) {
                window.dispatchEvent(new CustomEvent(PRINT_FAILED_EVENT, { detail: failed }))
              }
            })
            return
          }
          // px to mm at CSS's fixed 96dpi. Plus a little, because a rule that
          // lands exactly on the content's height still rounds the wrong way
          // on some drivers and spills a line onto a second page.
          printReceipt(tallest ? (tallest / 96) * 25.4 + 4 : undefined)
        })
      })
    },
    [],
  )

  /**
   * Loads a slip without sending it to the printer.
   *
   * Settling no longer prints, so without this the sale just taken would be
   * gone the moment it was filed, and Ctrl+P exists for exactly the moment a
   * customer asks for the slip after paying.
   */
  const hold = useCallback((order: Order, as: ReceiptKind = 'station') => {
    setKind(as)
    setReceipt(order)
  }, [])

  /** Ids whose slip has come off this till's printer; drives the badge. */
  const [printedIds, setPrintedIds] = useState<Set<string>>(getPrintedIds)
  const onPrinted = useCallback((id: string) => {
    setPrintedIds((prev) => new Set(prev).add(id))
  }, [])

  /**
   * Watching for waiters' tickets happens here, not on the sale screen.
   *
   * It used to live inside SalePage and so stopped the moment the cashier
   * opened Ayarlar or Rapor: an order rung in during that time never printed,
   * and the waiter arrived at a printer that had produced nothing.
   */
  const { queuePrint, claim } = useTicketPrinter({ onPrint: print, onPrinted })

  /** "Son fişler" penceresi; üst çubuktaki düğme olayla açıyor. */
  const [recentOpen, setRecentOpen] = useState(false)
  useEffect(() => {
    const open = () => setRecentOpen(true)
    window.addEventListener(RECENT_SLIPS_EVENT, open)
    return () => window.removeEventListener(RECENT_SLIPS_EVENT, open)
  }, [])

  return (
    <>
      <Routes>
        {/* Tek giriş adresi /giris: eski "/kurum/giris" bağlantıları oraya
            gidiyor. Giriş kurumu kimlikten buluyor, adreste bir önceki
            kurumun kodunun kalmasına gerek yok. */}
        <Route path="giris" element={<Navigate to="/giris" replace />} />
        {/* Reached from the login screen, and by the link an institution is
            sent with its code. No guard: whoever holds a code is exactly who
            this screen is for, and they have no session yet. */}
        <Route path="kurulum" element={<Navigate to="/kurulum" replace />} />

        <Route
          path=""
          element={
            <ProtectedRoute>
              <POSLayout title="Gel Al - Kasa">
                <SalePage
                  onPrint={print}
                  onHold={hold}
                  lastOrder={receipt}
                  queuePrint={queuePrint}
                  claimTicket={claim}
                  printedIds={printedIds}
                  onPrinted={onPrinted}
                />
              </POSLayout>
            </ProtectedRoute>
          }
        />

        {/* Ana ekran (18 Eki 2026): girişten sonra ve logoya basınca. Satış
            ekranı "" adresinde kalıyor; kasalardaki kiosk kurulumu oradan açılıyor.
            Koruma menüyle aynı: kasa yetkisi olmayan "/"a, oradan yerine. */}
        <Route
          path="ana"
          element={
            <MenuRoute>
              <POSLayout title="Ana Ekran" bare>
                <HomePage />
              </POSLayout>
            </MenuRoute>
          }
        />

        {/* Kasiyerin yazıcı ayarları (20 Eki 2026); patron Ayarlar'dan giriyor. */}
        <Route
          path="yazicilar"
          element={
            <MenuRoute>
              <POSLayout title="Yazıcılar">
                <PrintersPage />
              </POSLayout>
            </MenuRoute>
          }
        />

        <Route
          path="menu"
          element={
            <OwnerRoute perm="menu">
              <POSLayout title="Menü">
                <MenuAdminPage />
              </POSLayout>
            </OwnerRoute>
          }
        />

        {/* Inside the providers, like every other till screen: it reads the
            accounts through CariContext, which only subscribes where the
            feature is on. */}
        <Route
          path="cari"
          element={
            <CariRoute>
              <POSLayout title="Cari Hesap">
                <CariPage />
              </POSLayout>
            </CariRoute>
          }
        />

        <Route
          path="kiosk"
          element={
            <KioskRoute>
              <KioskPage />
            </KioskRoute>
          }
        />

        <Route
          path="mutfak"
          element={
            <KitchenRoute>
              <POSLayout title="Mutfak">
                <KitchenPage />
              </POSLayout>
            </KitchenRoute>
          }
        />

        <Route
          path="takip"
          element={
            <TrackRoute>
              <POSLayout title="Sipariş Takip">
                <TrackPage />
              </POSLayout>
            </TrackRoute>
          }
        />

        <Route
          path="rapor"
          element={
            <OwnerRoute perm="reports">
              {/* The full day's orders load only here, where they're shown. */}
              <ReportRoute onPrint={print} />
            </OwnerRoute>
          }
        />

        <Route
          path="masalar"
          element={
            <OwnerRoute perm="settings">
              <POSLayout title="Masalar">
                <SettingsPage onPrint={print} only="tables" />
              </POSLayout>
            </OwnerRoute>
          }
        />

        <Route
          path="ayarlar"
          element={
            <OwnerRoute perm="settings-page">
              <POSLayout title="Ayarlar">
                <SettingsPage onPrint={print} />
              </POSLayout>
            </OwnerRoute>
          }
        />

        <Route path="*" element={<Navigate to="" replace />} />
      </Routes>

      {/* Always mounted, only ever visible to the print stylesheet. */}
      {/* Kasa fişi yeni panelden kaydedilmemişse (splitByStation yok) ödeme
          fişi eskisi gibi garson fişinin ayarlarıyla, tezgahlara bölünerek
          basılıyor. */}
      <PrintErrorBanner />
      {recentOpen && <RecentSlipsDialog onPrint={print} onClose={() => setRecentOpen(false)} />}
      <Receipt
        order={receipt}
        tenantName={tenantName}
        kind={kind === 'cash' ? (cashReady ? 'cash' : 'station') : kind}
        settings={kind === 'cash' && cashReady ? cashReceiptSettings : receiptSettings}
      />
    </>
  )
}

/**
 * Rewrites a tenant-less URL to the same screen under this device's kurum.
 *
 * Every link handed out before institutions moved into the path lands here,
 * a bookmarked "/ayarlar", a "?yurt=" link, the bare domain. resolveTenantId
 * still reads the query, the subdomain and localStorage, so those all keep
 * naming the right institution; this only moves them to the address that
 * matches. The query string is carried over so "?yurt=" survives the hop and
 * still resolves on the other side.
 *
 * "/giris" and "/kurulum" are the exception, handled above rather than here:
 * those two screens ask for an identity, and the identity is what names the
 * institution. Sending them to whichever kurum this device happens to
 * remember would put somebody at a shop they do not work for.
 */
/**
 * Tarayıcıda düz adissoo.com tanıtım sitesini açar (30 Eki 2026, kullanıcı).
 * Ana ekrana eklenmiş uygulama (start_url "/"), iOS uygulaması, yerel ve
 * önizleme adresleri eskisi gibi kuruma gider; kurumlu adresler, /giris ve
 * QR menü buraya hiç uğramıyor.
 */
function opensSite(pathname: string, search: string): boolean {
  if (pathname !== '/' || search) return false
  if (window.location.hostname !== 'adissoo.com') return false
  const standalone =
    window.matchMedia?.('(display-mode: standalone)').matches ||
    (navigator as Navigator & { standalone?: boolean }).standalone === true
  return !standalone
}

function TenantRedirect() {
  const { pathname, search } = useLocation()
  const site = opensSite(pathname, search)
  useEffect(() => {
    if (site) window.location.replace('https://www.adissoo.com/')
  }, [site])
  if (site) return null
  const tenant = resolveTenantId() ?? FALLBACK_TENANT
  return <Navigate to={`/${tenant}${pathname}${search}`} replace />
}

function App() {
  /**
   * Starts the background sender for queued writes.
   *
   * Here rather than inside a screen: the queue has to keep draining while the
   * cashier is on Ayarlar or Rapor, and a sale rung in before the signal
   * dropped must still go out once it returns, whatever page happens to be
   * open at the time.
   */
  useEffect(() => startDrainer(), [])

  return (
    <Router>
      <AuthProvider>
        <UpgradeProvider>
        <Routes>
          {/*
            Outside OrdersProvider/CatalogProvider/OpenTicketsProvider/CartProvider
            on purpose. Those all subscribe the moment they see a tenantId, and
            the system administrator's tenantId ("yonetim") is not a real
            institution, letting them mount here would open live queries
            against a tenant that has no rows to return.
          */}
          <Route
            path="/sistem-9f3a"
            element={
              <SuperadminRoute>
                <SuperadminPage />
              </SuperadminRoute>
            }
          />
          {/* The customer's menu, reached by scanning a code on the table.
              Outside every provider on purpose: those all act on behalf of a
              signed-in member of an institution, and nobody is signed in here.
              Above the tenant routes because it spells its own institution out
              already. Two spellings because a printed code carries the tidy
              one and a pasted link carries the query. */}
          <Route path="/m/:tenantId" element={<MenuPage />} />
          {/* Masanın karekodu (QR Sipariş): menü + sipariş, garson, hesap. */}
          <Route path="/m/:tenantId/:masa" element={<MenuPage />} />
          <Route path="/m" element={<MenuPage />} />
          {/* Müşterinin sadakat kartı (30 Eki 2026): girişsiz, tokenla. */}
          <Route path="/k/:token" element={<LoyaltyCardPage />} />
          {/* Kiosk siparişinin takibi (31 Eki 2026): kiosk ekranındaki karekoddan. */}
          <Route path="/s/:token" element={<KioskTrackPage />} />
          {/* Yalnız yerelde: kiosk takibinin gösterimi (video, tasarım). */}
          {import.meta.env.DEV && <Route path="/dev/kiosk-done" element={<KioskDoneDemo />} />}
          {import.meta.env.DEV && <Route path="/dev/kiosk-takip" element={<KioskTrackDemo />} />}
          {import.meta.env.DEV && <Route path="/dev/kiosk-kasa" element={<KioskTillDemo />} />}
          {import.meta.env.DEV && <Route path="/dev/isletme" element={<TenantWindowDemo />} />}
          {import.meta.env.DEV && <Route path="/dev/bakiye" element={<BalanceDemo />} />}
          {import.meta.env.DEV && <Route path="/dev/gelir" element={<FinanceDemo />} />}
          {import.meta.env.DEV && <Route path="/dev/abonelik" element={<SubscriptionsDemo />} />}

          {/* Every till screen sits under its institution, so one domain
              serves them all and a shared link carries the kurum with it. */}
          <Route
            path="/:tenant/*"
            element={
              <OrdersProvider>
                <CatalogProvider>
                  <OpenTicketsProvider>
                    <CariProvider>
                      <CartProvider>
                        <Shell />
                      </CartProvider>
                    </CariProvider>
                  </OpenTicketsProvider>
                </CatalogProvider>
              </OrdersProvider>
            }
          />

          {/* Signing in and opening an institution both work without a kurum
              in the address: the identity typed on these screens is what says
              which institution it is. Above the catch-all so they are reached
              rather than redirected, and outside the providers, which would
              otherwise open live queries for whatever tenant the device last
              remembered. */}
          <Route path="/giris" element={<LoginPage />} />
          {/* Mutfak ekranı bağlama: QR, kod ya da paylaşılan bağlantı. */}
          <Route path="/ekran" element={<PairScreenPage />} />
          <Route path="/kurulum" element={<ActivatePage />} />
          {/* "Ücretsiz başla": tanıtım sitesinin düğmesi buraya geliyor. */}
          <Route path="/kayit" element={<ActivatePage trial />} />

          {/* Where an invitation or a password-reset mail lands. No kurum in
              the address: the link is all the person has, and the session it
              carries already says who they are. */}
          <Route path="/sifre" element={<SetPasswordPage />} />

          {/* Anything else with no institution in it: the bare domain, and
              every link handed out before the move ("/ayarlar", a "?yurt="
              link). Rewritten to the same screen under the kurum this device
              resolves to, so nothing anyone saved stops working. */}
          <Route path="*" element={<TenantRedirect />} />
        </Routes>
        </UpgradeProvider>
      </AuthProvider>
    </Router>
  )
}

export default App
